import * as React from 'react';
import { inject, observer } from 'mobx-react';
import * as Styled from './styled';
import {
    FormHelperText,
    MenuItem,
    TextField,
    Select,
    Checkbox,
    FormControlLabel,
    FormControl,
    InputLabel,
    Tooltip,
    IconButton
} from '@material-ui/core';
import { Receipt, KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons';
import AutoCompleteField from 'components/AutoCompleteField/AutoCompleteField';
import { ApiConsumer } from 'common/ApiProvider';
import RootAPI from 'api/interfaces/RootAPI';
import {
    ActionCode,
    AiNarrativeResponse,
    ApprovalStatus,
    Client,
    Code,
    CodeSetTemplate,
    CodeType,
    Employee,
    Features,
    Matter,
    NARRATIVE_VALIDATION_MESSAGES,
    NarrativeWarning,
    Role,
    TimeKeeperAssignment,
    TkOfficeOfficeName,
    WorkLocale
} from 'api/types/types';
import {
    setMatter,
    setNarrative,
    setWorkLocale,
    setPhase,
    setTask,
    setAct,
    setFFTask,
    setFFAct,
    setActionCode,
    setCodeSetTemplate,
    changeNarrativeLanguage,
    fetchPhaseCodes,
    fetchTaskCodes,
    fetchActivityCodes,
    fetchFFTaskCodes,
    fetchFFActCodes,
    fetchMatters,
    getMatterTooltipText,
    getWorkLocaleValue,
    setIncidentNumber,
    setAiUsageCount,
    setNoChangeRequiredCount,
    shouldSuppressFFFields,
    fetchFFActionCodesWithDefaultOnTop
} from 'util/timeEntry';
import DurationField from 'components/DurationField';
import NarrativeField from 'components/NarrativeField';
import { DateTime } from 'luxon';
import { MatterItemFormatter } from 'components/TemplateForm/TemplateForm';
import { InlineDatePicker } from 'material-ui-pickers';
import { FlexDiv } from 'common/flex';
import { StandardTextFieldProps } from '@material-ui/core/TextField';
import { ValidationState } from 'api/immutables/validators';
import { FeaturesConsumer } from 'common/FeaturesProvider';
import { getStringMatches, parseCode, parseName } from 'util/utils';
import { isoDate, getDateFormat } from 'util/date';
import { LinearProgressBar } from 'components/LoadingSpinner/LinearProgressBar';
import { TKConsumer } from 'common/TKProvider';
import { RootStore } from 'store/root.store';
import TimeEntryStore from 'store/timeentry.store';
import { withTranslation } from 'react-i18next';
import ImmutableTimeEntry from 'api/immutables/ImmutableTimeEntry';
import { theme } from 'common/theme';
import BillingRulesDialog from './BillingRulesDialog';

// new rejection roles should be added here, case-sensitive
const rejectionRoles = ['manager', 'projManager', 'admin'];

interface Props {
    timeEntry: ImmutableTimeEntry;
    isHighlighted?: boolean;
    onChange: (t: ImmutableTimeEntry, newVState?: ValidationState, durErrState?: boolean) => void;
    disabled?: boolean;
    showDate?: boolean;
    minHeight?: number;
    durValidationState?: boolean;
    validationState?: ValidationState;
    minNarrativeLength: number;
    maxNarrativeLength: number;
    actionCodesRequired: boolean;
    includeNarrative?: boolean;
    includeDuration?: boolean;
    onSetFieldLoader?: (value: boolean) => void;
    tkOfficesToShow: TkOfficeOfficeName[];
    isMgmtDashboard?: boolean;
    isEntryPanel?: boolean; // TODO: remove when enabling online editing in Management Dashboard
    auditLog?: ImmutableTimeEntry[];
    auditLogIndex?: number;
    rootStore?: RootStore;
    timeEntryStore?: TimeEntryStore;
    includeTaskActivityCheckbox?: boolean;
    aiResponse?: AiNarrativeResponse | null | string;
    setAiResponse?: (res: AiNarrativeResponse | null | string) => void;
    // tslint:disable-next-line:no-any
    t: any;
}
interface State {
    setFieldLoader: boolean;
    narrativeWarningText: string;
    minLength?: number;
    maxLength?: number;
}

@inject((allStores: { rootStore: RootStore }) => {
    let rootStore = allStores.rootStore;
    return {
        rootStore: rootStore,
        timeEntryStore: rootStore.timeEntryStore
    };
})
@observer
class TimeEntryForm extends React.Component<Props, State> {
    static defaultProps = {
        includeDuration: true,
        includeNarrative: true,
        includeTaskActivityCheckbox: true
    }
    state = {
        setFieldLoader: false,
        narrativeWarningText: '',
        minLength: undefined,
        maxLength: undefined
    }

    matterNarrativeWarningsKeys: string[] = [];
    matterNarrativeWarningsList: NarrativeWarning[] = [];
    narrativeWarnings: Set<string> = new Set('');

    componentDidMount() {
        this.onLoad();
    }

    componentWillUnmount() {
        this.props.timeEntryStore!.resetNarrativePreviousPopulatedCodes();
    }

    onLoad = async() => {
        const { timeEntry, onChange, rootStore } = this.props;
        if (timeEntry.matterId && timeEntry.isDraft()) {
            const matter = await rootStore!.api.Matter.get(timeEntry.matterId);
            if (matter) {
                timeEntry.blockBillingWords = matter.blockBillingWords;
                timeEntry.bannedWords = matter.bannedWords;
                this.setState({
                    minLength: matter.minLength,
                    maxLength: matter.maxLength
                });
                if (matter.narrativeWarnings) {
                    this.matterNarrativeWarningsKeys = matter.narrativeWarnings.map(w => w.bannedWord);
                    this.matterNarrativeWarningsList = matter.narrativeWarnings;
                    if (timeEntry.narrative) {
                        const matches = getStringMatches(timeEntry.narrative, this.matterNarrativeWarningsKeys);
                        this.updateNarrativeWarnings(matches);
                    }
                }
                onChange(timeEntry);
            }
        }
    }

    _setMatter = async (m: Matter | null) => {
        const { timeEntry, onChange, validationState, timeEntryStore } = this.props;
        this.setState({ setFieldLoader: true });
        this.resetNarrativeWarnings();
        timeEntryStore!.resetNarrativePreviousPopulatedCodes();
        if (m) {
            if (m.narrativeWarnings) {
                this.matterNarrativeWarningsKeys = m.narrativeWarnings.map(w => w.bannedWord);
                this.matterNarrativeWarningsList = m.narrativeWarnings;
                if (timeEntry.narrative) {
                    const matches = getStringMatches(timeEntry.narrative, this.matterNarrativeWarningsKeys);
                    this.updateNarrativeWarnings(matches);
                }
            }
        }
        const entry = this.props.timeEntry.setShowBillingRules(false);
        await setMatter(m, entry, onChange, validationState);
        this.onLoad();
        this.setState({ setFieldLoader: false });
    }

    resetNarrativeWarnings = () => {
        this.narrativeWarnings.clear();
        this.matterNarrativeWarningsKeys = [];
        this.matterNarrativeWarningsList = [];
        this.setState({ narrativeWarningText: '' });
    }

    _setNarrative = (text: string, acceptedAi: boolean) => {
        const { timeEntry, onChange, validationState } = this.props;
        setNarrative(text, timeEntry, onChange, validationState, acceptedAi);
        if (this.matterNarrativeWarningsKeys) {
            const matches = getStringMatches(text, this.matterNarrativeWarningsKeys);
            this.updateNarrativeWarnings(matches);
        }
    }

    updateNarrativeWarnings = (matches: string[]) => {
        const diff = matches.length - this.narrativeWarnings.size;
        if (diff === 0) {
            return;
        } else if (diff > 0) {
            matches.map((item: string) => {
                if (!this.narrativeWarnings.has(item)) {
                    this.narrativeWarnings.add(item);
                }
            });
        } else {
            Array.from(this.narrativeWarnings).map((item: string) => {
                if (!matches.includes(item)) {
                    this.narrativeWarnings.delete(item);
                }
            });
        }
        const narrativeWarningText: string[] = this.matterNarrativeWarningsList
            .filter((obj: NarrativeWarning) => obj.bannedWord.toLowerCase() === Array.from(this.narrativeWarnings).pop())
            .map((obj: NarrativeWarning) => obj.responseMessage);
        this.setState({ narrativeWarningText: narrativeWarningText[0] });
    }

    getNarrativeWarningElement = () => (
        this.state.narrativeWarningText ? (
            <p
                style={{
                    color: theme.warning,
                    margin: 'auto'
                }}
            >
                {this.state.narrativeWarningText}
            </p>
        ) : null
    );

    setClient = (c?: Client | null) => {
        let entry = this.props.timeEntry.setClient(c);
        this.props.onChange(entry);
        this.setState({ narrativeWarningText: '' });
        this.resetNarrativeWarnings();
    }

    _setActionCode = (c: ActionCode | null) => {
        const { timeEntry, onChange, validationState } = this.props;
        setActionCode(c, timeEntry, onChange, validationState);
        if (timeEntry.narrative && this.matterNarrativeWarningsKeys) {
            const matches = getStringMatches(timeEntry.narrative, this.matterNarrativeWarningsKeys);
            this.updateNarrativeWarnings(matches);
        }
    }

    setDuration = (dur: number, err?: boolean) => {
        let newVState;
        dur = (dur < 0) ? 0 : dur;
        let entry = this.props.timeEntry.setDuration(dur);

        if (this.props.validationState) {
            newVState = this.props.validationState!.clone();
            newVState.zeroDuration = false;
            newVState.twentyFourDuration = false;
        }
        this.props.onChange(entry, newVState, err);
    }
    
    setWorkDate = (d: Date) => {
        const date: DateTime = DateTime.fromJSDate(d);
        let entry = this.props.timeEntry.setWorkDate(date);
        this.props.onChange(entry);
    }

    _setCodeSetTemplate = async (c: CodeSetTemplate | null) => {
        const { timeEntry, onChange, validationState } = this.props;
        setCodeSetTemplate(c, timeEntry, onChange, validationState);
        if (timeEntry.narrative && this.matterNarrativeWarningsKeys) {
            const matches = getStringMatches(timeEntry.narrative, this.matterNarrativeWarningsKeys);
            this.updateNarrativeWarnings(matches);
        }
    }

    setEmployee = async (emp: Employee | null) => {
        const { timeEntry, rootStore, onChange } = this.props;
        let office, officeName;
        const previousEmployeeSelection = timeEntry.employee;
        let entry = timeEntry.setEmployee(emp);
        if (!emp || (previousEmployeeSelection && emp.timeKeeperId !== previousEmployeeSelection.timeKeeperId)) {
            entry = entry.setClient(null)
            .setWorkLocaleId(null)
            .setDuration(0)
            .setNarrative('');
        }
        if (emp) {
            const actTk = await rootStore!.managementDashboardStore.getActiveTkForUser(entry);
            if (actTk) {
                office = actTk.office;
                officeName = actTk.officeName;
            }
        }
        entry = entry.setOffice(office).setOfficeName(officeName);
        onChange(entry);
    }

    handleOfficeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        let tempOfficeName = this.props.tkOfficesToShow.filter(x => x.office === event.target.value)[0].officeName;
        // Changing both office and office name here
        let entry = this.props.timeEntry.setOffice(event.target.value).setOfficeName(tempOfficeName);
        this.props.onChange(entry);
    }

    getCheckboxMessageKey = (entry: ImmutableTimeEntry) => {
        return (entry.isPhaseCode && entry.isActCode) ? 'task_activity' :
            entry.isPhaseCode ? 'task' :
                entry.isActCode ? 'activity' : 'task_activity';
    }

    handleIncludeCodesChange = (e: React.ChangeEvent, checked: boolean) => {
        const { timeEntry, timeEntryStore, onChange } = this.props;
        let entry = timeEntry.clone();
        timeEntryStore!.setNarrativeAutoPopulate(checked);
        if (checked) {
            if (entry.actCodeId && entry.actCode && entry.actCodeDesc) {
                const actObj: Code = {
                    id: entry.actCodeId,
                    name: entry.actCode,
                    description: entry.actCodeDesc,
                    type: CodeType.ACT
                };
                entry = timeEntryStore!.setNarrativeFromCodes(entry, actObj);
            }
            if (entry.taskCodeId && entry.taskCode && entry.taskCodeDesc) {
                const taskObj: Code = {
                    id: entry.taskCodeId,
                    name: entry.taskCode,
                    description: entry.taskCodeDesc,
                    type: CodeType.TASK
                };
                entry = timeEntryStore!.setNarrativeFromCodes(entry, taskObj);
            }
            onChange(entry);
        }
    }

    handleBillableChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        let entry = this.props.timeEntry.setBillable(event.target.value === 'true');
        this.props.onChange(entry);
    }

    checkHighlight(field: string) {
        const {auditLog, auditLogIndex, timeEntry } = this.props;
        if (auditLog && auditLog.length > 0) {
            // check if it is not the first entry in audit then highlight changes
            if (auditLogIndex && auditLogIndex > 0) {
                // compare current entry with prevoius audit
                const previousAudit = auditLog[auditLogIndex - 1];
                return timeEntry[field as keyof ImmutableTimeEntry] !== previousAudit[field as keyof ImmutableTimeEntry];
            }
        }
        return false;
    }

    toggleBillingRules = () => {
        const newShowBillingRules = !this.props.timeEntry.showBillingRules;
        const entry = this.props.timeEntry.setShowBillingRules(newShowBillingRules);
        this.props.onChange(entry)
    }

    render() {
        const { setFieldLoader, narrativeWarningText } = this.state;
        const {
            timeEntry,
            isHighlighted,
            disabled,
            showDate,
            includeNarrative,
            includeDuration,
            includeTaskActivityCheckbox,
            durValidationState,
            validationState,
            tkOfficesToShow,
            isMgmtDashboard,
            isEntryPanel, // TODO: remove when enabling online editing in Management Dashboard
            aiResponse,
            setAiResponse,
            onChange,
            rootStore,
            t
        } = this.props;
        const matterLabel = rootStore!.appStore.features.EpochConfigMatterLabel;
        const isProject = rootStore!.appStore.isProject;
        const isDisabledFields = (!isMgmtDashboard && timeEntry.isPosted()) ||
            timeEntry.approvalStatus === ApprovalStatus.APPROVED ||
            (timeEntry.approvalStatus === ApprovalStatus.REJECTED && timeEntry.isPosted()) ||
            (isMgmtDashboard && !timeEntry.employee) ||
            (isMgmtDashboard && isEntryPanel); // TODO: remove last condition clause when enabling online editing in Management Dashboard
        let conflictingBannedWords = [] as string[];
        let conflictingBlockBillingWords = [] as string[];
        let durErrTxt = '';
        let matterStatusErrText: string;
        if (validationState) {
            conflictingBannedWords = validationState.narrativeBannedWords;
            conflictingBlockBillingWords = validationState.narrativeBlockBillingWords;
            if (validationState.twentyFourDuration) {
                durErrTxt = t('validation.duration.exceed_24');
            } 
            if (validationState.zeroDuration) {
                durErrTxt = t('validation.duration.zero');
            }
        }
        if (durValidationState) {
            durErrTxt = t('validation.duration.invalid');
        }
        let bwText = (conflictingBannedWords.length > 0) ? `${conflictingBannedWords.join(', ')}` : '';
        let bbText = (conflictingBlockBillingWords.length > 0) ? `${conflictingBlockBillingWords.join(', ')}` : '';
        let narBwBbWords = bwText ? `${bwText}` + (bbText && ', ') + `${bbText}` : `${bbText}`;
        let narHelperText = (bwText || bbText) && 
            t(NARRATIVE_VALIDATION_MESSAGES.BANNED_BLOCKING_WORDS, { narBwBbWords });
        let narrativeErrored = (validationState && validationState.narrativeLength) ||
            (validationState && validationState.maxNarrativeLength) ||
            ((conflictingBannedWords.length + conflictingBlockBillingWords.length) > 0) ||
            !!narrativeWarningText;
        let invDur = (validationState ? validationState.duration : false);
        if (validationState && validationState.narrativeLength) {
            const minLength = validationState.narrativeMinLength;
            narHelperText = t(NARRATIVE_VALIDATION_MESSAGES.MIN_LENGTH, { minLength });
        }
        if (validationState && validationState.matterStatusInvalid) {
            matterStatusErrText = `${t('form.validation.matter.status', { matterLabel })}: ${timeEntry.matterStatusDesc}`;
        }
        if (validationState && validationState.matterEntryTypeInvalid) {
            matterStatusErrText = t('form.validation.matter.type_cost', { matterLabel });
        }
        if (validationState && validationState.maxNarrativeLength) {
            const maxLength = validationState.narrativeMaxLength;
            narHelperText = t(NARRATIVE_VALIDATION_MESSAGES.MAX_LENGTH, { maxLength });
        }
        const defaultIncUnit = rootStore!.appStore.features.EpochConfigTimeEntryUnitSecs || 360;
        let increment: number =  Math.floor(defaultIncUnit / 0.36) / 10000;
        if (timeEntry!.timeEntryUnit === 'Z1') {
            increment = 0.1;
        } else if (timeEntry!.timeEntryUnit === 'Z2') {
            increment = 0.25;
        } else if (timeEntry!.timeEntryUnit === 'Z3') {
            increment = 0.05;
        } else if (timeEntry!.timeEntryUnit === 'Z4') {
            increment = 0.0166;
        }
        const officeCode = parseCode(timeEntry.office, timeEntry.officeName);
        const isActivityTask = this.props.timeEntryStore!.narrativeAutoPopulate;
        const defaultFFActionCode = rootStore!.appStore.features.EpochConfigDefaultFFActionCode;
        return (
            <FeaturesConsumer>
                { (features: Features) =>
                <ApiConsumer>
                    { (api: RootAPI) =>
                <TKConsumer>
                    { (tk: TimeKeeperAssignment) =>
                        <FlexDiv direction="column">
                            <Styled.FieldContainer minHeight={this.props.minHeight}>
                                <Styled.Fields>
                                    {showDate &&
                                        <InlineDatePicker
                                            format={timeEntry ?
                                                DateTime.fromISO(timeEntry.workDateTime).toFormat(getDateFormat())
                                                : 'MM/dd/yyyy'}
                                            onlyCalendar={true}
                                            value={timeEntry ? isoDate(DateTime.fromISO(timeEntry.workDateTime)) : ''}
                                            disabled={timeEntry && timeEntry.isPosted() || disabled || !tk.writable}
                                            onChange={this.setWorkDate}
                                            leftArrowIcon={<KeyboardArrowLeft/>}
                                            rightArrowIcon={<KeyboardArrowRight/>}
                                            TextFieldComponent={(props: StandardTextFieldProps) =>
                                                <TextField
                                                    {...props}
                                                    style={{justifyContent: 'flex-end'}}
                                                />
                                            }
                                        />
                                    }
                                    {isMgmtDashboard &&
                                        <AutoCompleteField
                                            label={t('form.field.timekeeper', { ns: 'management_dashboard' })}
                                            errorMessage={t('form.validation.timekeeper.invalid', { ns: 'management_dashboard' })}
                                            errored={validationState ? validationState.missing.tk : false}
                                            fetch={(...props) => api.ManagementDashboard.getEmployees(
                                                rootStore!.managementDashboardStore.selectedRole,
                                                rootStore!.managementDashboardStore.approvalTimekeeperId,
                                                ...props,
                                                DateTime.fromISO(timeEntry.workDateTime).toISODate()
                                            )}
                                            currentItem={timeEntry.employee ? timeEntry.employee : null}
                                            getItemText={(emp: Employee) => `${emp.timeKeeperId} - ${emp.name}`}
                                            clearable={true}
                                            onSelect={this.setEmployee}
                                            onClear={() => this.setEmployee(null)}
                                            disabled={disabled || timeEntry.isPosted() || !tk.writable || !!timeEntry.id}
                                        />
                                    }
                                    <AutoCompleteField
                                        label={t('field.client')}
                                        fetch={api.Client.searchClients}
                                        currentItem={timeEntry.client ? timeEntry.client : null}
                                        getItemText={(c: Client) => parseCode(c.name, c.number)}
                                        clearable={true}
                                        onClear={() => this.setClient(null)}
                                        onSelect={this.setClient}
                                        disabled={disabled || isDisabledFields || !tk.writable}
                                        highlighted={this.checkHighlight('clientId')}
                                    />
                                    {features.EpochConfigTimeEntriesMattersRequired &&
                                        <Styled.MatterContainer draft={timeEntry.isDraft()}>
                                            <div>
                                                <AutoCompleteField
                                                    label={t('field.matter', { matterLabel })}
                                                    errorMessage={t('form.validation.matter.invalid', { matterLabel })}
                                                    errored={validationState ? validationState.missing.matter : false}
                                                    fetch={(...props) => fetchMatters(timeEntry, ...props)}
                                                    currentItem={timeEntry.matter}
                                                    clearable={true}
                                                    onClear={() => this._setMatter(null)}
                                                    formatItem={MatterItemFormatter}
                                                    getItemText={(m: Matter) => parseCode(m.name, m.number)}
                                                    onSelect={this._setMatter}
                                                    disabled={disabled || isDisabledFields || !tk.writable}
                                                    tooltip={getMatterTooltipText}
                                                    highlighted={this.checkHighlight('matterId')}
                                                />
                                                {setFieldLoader && <LinearProgressBar color={'primary'} progressBar={50}/>}
                                            </div>
                                            {timeEntry.isDraft() &&
                                                <Tooltip title={t('form.icon.billing_rules.tooltip')}>
                                                    <Styled.BillingRulesIcon
                                                        onClick={this.toggleBillingRules}
                                                        disabled={!timeEntry.matterId || setFieldLoader}
                                                    >
                                                        <Receipt fontSize="small"/>
                                                    </Styled.BillingRulesIcon>
                                                </Tooltip>
                                            }
                                        </Styled.MatterContainer>
                                    }
                                    {tkOfficesToShow.length > 1 ?
                                        <Select
                                            title={officeCode}
                                            displayEmpty={true}
                                            name={'select-office'}
                                            value={timeEntry.office}
                                            disabled={disabled || isDisabledFields || !tk.writable}
                                            onChange={this.handleOfficeChange}
                                            style={{paddingTop: `16px`, color: this.checkHighlight('office') ? theme.summary.blue : ''}}
                                        >
                                            {tkOfficesToShow.map((tko: TkOfficeOfficeName) => {
                                                const tkOfficeCode = parseCode(tko.office, tko.officeName);
                                                return (
                                                    <MenuItem value={tko.office} key={tko.office} title={tkOfficeCode}>
                                                        {tkOfficeCode}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    :
                                        <TextField
                                            title={officeCode}
                                            label={t('form.field.office.label')}
                                            disabled={true}
                                            value={officeCode}
                                            inputProps={{
                                                style: this.checkHighlight('office') ? {color: theme.summary.blue} : {}
                                            }}
                                            InputLabelProps={{
                                                style: this.checkHighlight('office') ? {color: theme.summary.blue} : {}
                                            }}
                                        // TODO fetch offices from user, disable iff there is only one
                                        />
                                    }
                                    {features.EpochConfigWorkLocaleEnabled &&
                                        <AutoCompleteField
                                            label={t('form.field.work_location.label')}
                                            fetch={(search: string) => api.TimeEntry.searchWorkLocales(search)}
                                            currentItem={timeEntry.workLocaleId ? getWorkLocaleValue(timeEntry) : null}
                                            getItemText={(w: WorkLocale) => parseCode(w.localeSearch, null)}
                                            clearable={true}
                                            onClear={() => setWorkLocale(null, timeEntry, onChange, validationState)}
                                            onSelect={(w: WorkLocale) => setWorkLocale(w, timeEntry, onChange, validationState)}
                                            disabled={disabled || isDisabledFields || !tk.writable || setFieldLoader}
                                            errorMessage={t('form.validation.work_location')}
                                            errored={validationState ? validationState.missing.workLocale : false}
                                            highlighted={this.checkHighlight('workLocaleId')}
                                        />
                                    }
                                    {/** TODO figure out when to display phase and tasks sets */}
                                    {(timeEntry.matter && !isDisabledFields) && features.EpochConfigCodeSetTemplatesEnabled &&
                                        <AutoCompleteField
                                            label={t('form.field.code_template.label')}
                                            fetch={(...props) => api.CodeSet.getCodeSets(timeEntry.matterId!, ...props)}
                                            currentItem={timeEntry.selectedCodeSetTemplate}
                                            clearable={true}
                                            disabled={isDisabledFields || disabled || !tk.writable}
                                            onClear={() => this._setCodeSetTemplate(null)}
                                            getItemText={(cs: CodeSetTemplate) => parseCode(cs.codeSetName, cs.codeSetRef)}
                                            onSelect={this._setCodeSetTemplate}
                                        />
                                    }
                                    {(timeEntry.isPhaseCode || (timeEntry.isPosted() && timeEntry.phaseId)) &&
                                        <>
                                            <AutoCompleteField
                                                errorMessage={t('form.validation.phase')}
                                                errored={validationState ? validationState.missing.phase : false}
                                                label={t('form.field.phase.label')}
                                                currentItem={timeEntry.phase}
                                                clearable={true}
                                                disabled={
                                                    timeEntry.matterId === null ||
                                                    disabled ||
                                                    isDisabledFields || !tk.writable}
                                                onClear={() => setPhase(null, timeEntry, onChange, validationState)}
                                                onSelect={(c: Code) => setPhase(c, timeEntry, onChange, validationState)}
                                                fetch={(searchText) => fetchPhaseCodes(timeEntry, searchText)}
                                                getItemText={(c: Code) => parseCode(c.name, c.description)}
                                                highlighted={this.checkHighlight('phaseId')}
                                            />
                                            <AutoCompleteField
                                                errorMessage={t('form.validation.task')}
                                                errored={validationState ? validationState.missing.task : false}
                                                label={t('form.field.task.label')}
                                                currentItem={timeEntry.task}
                                                clearable={true}
                                                onSelect={(c: Code) => setTask(c, timeEntry, onChange, validationState)}
                                                onClear={() => setTask(null, timeEntry, onChange, validationState)}
                                                fetch={(searchText) => fetchTaskCodes(timeEntry, searchText)}
                                                disabled={timeEntry.phase === null || disabled || isDisabledFields || !tk.writable}
                                                getItemText={(c: Code) => parseCode(c.name, c.description)}
                                                highlighted={this.checkHighlight('taskCodeId')} 
                                            />
                                            {timeEntry.taskCodeTicketRequired && (
                                                <TextField
                                                    label={t('form.field.incident_number.label')}
                                                    fullWidth={true}
                                                    value={timeEntry.reference || ''}
                                                    inputProps={{
                                                        maxLength: 20,
                                                        'aria-label': t('form.field.incident_number.label')
                                                    }}
                                                    onChange={(e) => setIncidentNumber(e.target.value, timeEntry, onChange, validationState)}
                                                    error={validationState ? validationState.missing.incidentNumber : false}
                                                    disabled={disabled || isDisabledFields || !tk.writable}
                                                    helperText={(validationState && validationState.missing.incidentNumber) ? t('form.validation.incident_number') : ''}
                                                />
                                            )}
                                            {isProject &&
                                                <FormControl
                                                    fullWidth={true}
                                                    disabled={disabled || isDisabledFields || !tk.writable}
                                                >
                                                    <InputLabel
                                                        shrink={typeof timeEntry.billable === 'boolean'}
                                                        style={this.checkHighlight('billable') ? {color: theme.summary.blue} : {}}
                                                    >
                                                        {t('form.field.billable.label')}
                                                    </InputLabel>
                                                    <Select
                                                        displayEmpty={true}
                                                        name={'select-billable'}
                                                        value={timeEntry.billable !== null ? timeEntry.billable : undefined}
                                                        onChange={this.handleBillableChange}
                                                        style={this.checkHighlight('billable') ? {color: theme.summary.blue} : {}}
                                                    >
                                                        <MenuItem value={'false'}>{t('form.field.billable.option.no')}</MenuItem>
                                                        <MenuItem value={'true'}>{t('form.field.billable.option.yes')}</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            }
                                        </>
                                    }
                                    {(timeEntry.isActCode || (timeEntry.isPosted() && timeEntry.actCodeId)) &&
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.activity')}
                                            errored={validationState ? validationState.missing.activity : false}
                                            label={t('form.field.activity.label')}
                                            currentItem={timeEntry.activity}
                                            clearable={true}
                                            onClear={() => setAct(null, timeEntry, onChange, validationState)}
                                            onSelect={(c: Code) => setAct(c, timeEntry, onChange, validationState)}
                                            fetch={(searchText) => fetchActivityCodes(timeEntry, searchText)}
                                            disabled={!timeEntry.matterId || disabled || isDisabledFields || !tk.writable}
                                            getItemText={(c: Code) => parseCode(c.name, c.description)}
                                            highlighted={this.checkHighlight('actCodeId')}
                                        />
                                    }
                                    {((this.props.actionCodesRequired || defaultFFActionCode) || (timeEntry.isPosted() && timeEntry.actionCodeId)) &&
                                        <AutoCompleteField
                                            errorMessage={t('form.validation.action')}
                                            errored={validationState ? validationState.missing.action : false}
                                            label={t('form.field.action_code.label')}
                                            currentItem={timeEntry.actionCodeObj}
                                            disabled={disabled || isDisabledFields || !tk.writable}
                                            clearable={true}
                                            onClear={() => this._setActionCode(null)}
                                            onSelect={this._setActionCode}
                                            fetch={(searchText) =>
                                                defaultFFActionCode
                                                    ? fetchFFActionCodesWithDefaultOnTop(searchText)
                                                    : api.Code.getActionCodes(timeEntry.matterId!, undefined, searchText)
                                            }
                                            getItemText={(code: ActionCode) =>
                                                `${code.actionCode}${code.actionText ? `
                                                    - ${code.actionText}` : ''}`
                                            }
                                            highlighted={this.checkHighlight('actionCodeId')}
                                        />
                                    }
                                    {features.EpochConfigFlatFeeCodesEnabled &&
                                        (timeEntry.isFfTaskCode || (timeEntry.isPosted() && timeEntry.ffTaskCodeId)) &&
                                        !shouldSuppressFFFields(timeEntry)
                                        &&
                                        <>
                                            <AutoCompleteField
                                                errorMessage={t('form.validation.ff_task')}
                                                errored={validationState ? validationState.missing.ffTask : false}
                                                label={t('form.field.ff_task.label')}
                                                currentItem={timeEntry.ffTask}
                                                clearable={true}
                                                onClear={() => setFFTask(null, timeEntry, onChange, validationState)}
                                                onSelect={(c: Code) => setFFTask(c, timeEntry, onChange, validationState)}
                                                fetch={(searchText) => fetchFFTaskCodes(timeEntry, searchText)}
                                                getItemText={(c: Code) => parseCode(c.name, c.description)}
                                                disabled={disabled || isDisabledFields || !tk.writable}
                                                highlighted={this.checkHighlight('ffTaskCodeId')}
                                            />
                                            <AutoCompleteField
                                                errorMessage={t('form.validation.ff_activity')}
                                                errored={validationState ? validationState.missing.ffAct : false}
                                                label={t('form.field.ff_activity.label')}
                                                currentItem={timeEntry.ffActivity}
                                                disabled={!timeEntry.ffTaskCodeId || disabled || isDisabledFields || !tk.writable}
                                                clearable={true}
                                                onClear={() => setFFAct(null, timeEntry, onChange, validationState)}
                                                onSelect={(c: Code) => setFFAct(c, timeEntry, onChange, validationState)}
                                                fetch={(searchText) => fetchFFActCodes(timeEntry, searchText)}
                                                getItemText={(c: Code) => parseCode(c.name, c.description)}
                                                highlighted={this.checkHighlight('ffActCodeId')}
                                            />
                                        </>
                                    }
                                </Styled.Fields>
                            </Styled.FieldContainer>
                            <FlexDiv flex={1} />
                            <Styled.FieldContainer
                                minHeight={this.props.minHeight}
                                style={{display: 'grid', alignItems: 'end'}}
                            >
                                <Styled.NarrativeToolbar>
                                    {includeDuration &&
                                        <DurationField
                                            duration={timeEntry!.duration}
                                            increment={increment}
                                            onChange={this.setDuration}
                                            error={invDur || durValidationState}
                                            errorText={durErrTxt}
                                            disabled={disabled || isDisabledFields || !tk.writable}
                                            color={this.checkHighlight('duration') ? theme.summary.blue : ''}
                                        />
                                    }
                                    {features.EpochConfigTaskActivityCheckboxEnabled && includeTaskActivityCheckbox && timeEntry.isDraft() &&
                                        <Styled.NarrativeCheckbox>
                                            <FormControlLabel
                                                disabled={isDisabledFields || !(timeEntry.isPhaseCode || timeEntry.isActCode)}
                                                control={
                                                    <Checkbox
                                                        checked={isActivityTask}
                                                        onChange={this.handleIncludeCodesChange}
                                                    />
                                                }
                                                label={t(`form.checkbox.auto_populate.${this.getCheckboxMessageKey(timeEntry)}`)}
                                            />
                                        </Styled.NarrativeCheckbox>
                                    }
                                </Styled.NarrativeToolbar>
                                <Styled.BottomFields>
                                    {includeNarrative &&
                                        <Styled.NarrativeFieldContainer
                                            isHighlighted={isHighlighted}
                                            color={this.checkHighlight('narrative') ? theme.summary.blue : ''}
                                            aria-label={timeEntry.billingLangText ?
                                                `${t('form.field.narrative.label')} (${timeEntry.billingLangText})`
                                                :
                                                t('form.field.narrative.label')
                                            }
                                        >
                                            <NarrativeField
                                                helperText={narHelperText.length > 0 ? narHelperText : this.getNarrativeWarningElement()}
                                                error={narrativeErrored}
                                                value={timeEntry.narrative}
                                                placeholder={timeEntry.billingLangText ?
                                                    `${t('form.field.narrative.label')} (${timeEntry.billingLangText})`
                                                    :
                                                    t('form.field.narrative.label')
                                                }
                                                onChange={this._setNarrative}
                                                disabled={disabled || isDisabledFields || !tk.writable}
                                                maxLength={features.EpochConfigNarrativesMaximumChars}
                                                dictionaryKey={timeEntry.matterBillingLangDictionary}
                                                changeLanguage={(key: string) => changeNarrativeLanguage(key, timeEntry, onChange)}
                                                enableAI={true}
                                                timeEntry={timeEntry}
                                                aiResponse={aiResponse}
                                                setAiResponse={setAiResponse}
                                                aiOnPost={rootStore!.narrativeStore.aiOnPostEnabled &&
                                                    validationState && validationState.narrativeMatterInvalid}
                                                updateAiUsageCount={() => setAiUsageCount(timeEntry, onChange, validationState)}
                                                updateNoChangeRequiredCount={() => setNoChangeRequiredCount(timeEntry, onChange)}
                                                aiUsageCount={timeEntry.aiUsageCount}
                                            />
                                        </Styled.NarrativeFieldContainer>
                                    }
                                </Styled.BottomFields>
                                {timeEntry.actionResponse &&
                                    <FormHelperText error={true}>
                                        {timeEntry.actionResponse}
                                    </FormHelperText>
                                }
                                {matterStatusErrText &&
                                    <FormHelperText error={true}>
                                        {matterStatusErrText}
                                    </FormHelperText>
                                }
                                {timeEntry.rejectionCode && timeEntry.isDraft() &&
                                    <FormHelperText error={true}>
                                        {`${t('form.validation.rejection_code.label', { ns: 'management_dashboard' })}
                                            ${parseCode(timeEntry.rejectionCode, timeEntry.rejectionDesc)}`}
                                    </FormHelperText>
                                }
                            </Styled.FieldContainer>
                            {isMgmtDashboard && timeEntry.isPosted() &&
                                <Styled.FieldContainer>
                                        <Styled.ApprovalContainer>
                                            <Styled.ApprovalHeader>
                                                {t('form.field.approval.label', { ns: 'management_dashboard' })}
                                            </Styled.ApprovalHeader>
                                            <div>{t('form.field.approval.column.role', { ns: 'management_dashboard' })}</div>
                                            <div>{t('form.field.approval.column.reviewed_by', { ns: 'management_dashboard' })}</div>
                                            <div>{t('form.field.approval.column.status', { ns: 'management_dashboard' })}</div>
                                            {rejectionRoles.map(rejRole =>
                                                rootStore!.appStore.approvalStages[Role[rejRole.toUpperCase()]] ?
                                                    <>
                                                        <div>{rootStore!.appStore.approvalStages[Role[rejRole.toUpperCase()]]}</div>
                                                        <div>{parseName(`${timeEntry['approvedBy' + rejRole.charAt(0).toUpperCase() + rejRole.substring(1) + 'Name']}`)}</div>
                                                        <div>
                                                            {`${timeEntry[rejRole + 'ApprovalStatus']}
                                                                ${timeEntry[rejRole + 'ApprovalStatus'] === ApprovalStatus.REJECTED && timeEntry.rejectionCode ?
                                                                    '(' + parseCode(timeEntry.rejectionCode, timeEntry.rejectionDesc) + ')'
                                                                :
                                                                    ''
                                                                }
                                                            `}
                                                        </div>
                                                    </>
                                                :
                                                null
                                            )}
                                        </Styled.ApprovalContainer>
                                </Styled.FieldContainer>
                            }
                            <BillingRulesDialog
                                timeEntry={this.props.timeEntry}
                                minLength={this.state.minLength}
                                maxLength={this.state.maxLength}
                                close={this.toggleBillingRules}
                            />
                        </FlexDiv>
                    }
                </TKConsumer>}
                    </ApiConsumer>
                }
            </FeaturesConsumer>
        );
    }
}

export default withTranslation(['timeentries', 'management_dashboard'])(TimeEntryForm);